import React from 'react';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { RouteMetadata } from './components/SEO';
import { PageTransition } from './components/PageTransition';
import { AdPrivacyProvider } from './components/AdPrivacy';
import { resolveRoute } from './lib/routes';
import { loadPageComponent, type PageComponent } from './lib/route-loader';

interface AppProps {
  initialPath?: string;
  initialPage?: PageComponent;
  pageLoader?: typeof loadPageComponent;
}

export default function App({ initialPath, initialPage, pageLoader = loadPageComponent }: AppProps) {
  const [route, setRoute] = React.useState(() => resolveRoute(initialPath || (typeof window === 'undefined' ? '/' : window.location.pathname + window.location.search + window.location.hash)));
  const [pageComponent, setPageComponent] = React.useState<PageComponent | null>(() => initialPage || null);
  const [loadError, setLoadError] = React.useState(false);
  const [retry, setRetry] = React.useState(0);
  const loadedPageType = React.useRef(initialPage ? route.type : null);
  const needsFocus = React.useRef(false);
  const routeKey = [route.type, route.id, route.slug].join(':');

  React.useEffect(() => {
    const current = window.location.pathname + window.location.search + window.location.hash;
    if (route.href !== current) window.history.replaceState({}, '', route.href);
  }, []);

  const changeRoute = React.useCallback((path: string) => {
    const next = resolveRoute(path);
    if (next.type !== loadedPageType.current) setPageComponent(null);
    setLoadError(false);
    needsFocus.current = true;
    setRoute(next);
  }, []);

  const navigate = (path: string) => {
    const next = resolveRoute(path);
    window.history.pushState({}, '', next.href);
    changeRoute(next.href);
  };

  React.useEffect(() => {
    const onPopState = () => changeRoute(window.location.pathname + window.location.search + window.location.hash);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [changeRoute]);

  React.useEffect(() => {
    if (loadedPageType.current === route.type && pageComponent) return;
    let cancelled = false;
    setLoadError(false);
    void pageLoader(route.type).then(component => {
      if (cancelled) return;
      loadedPageType.current = route.type;
      setPageComponent(() => component);
    }).catch(() => {
      if (!cancelled) setLoadError(true);
    });
    return () => { cancelled = true; };
  }, [route.type, pageComponent, retry, pageLoader]);

  React.useEffect(() => {
    if ((!pageComponent && !loadError) || !needsFocus.current) return;
    needsFocus.current = false;
    const frame = window.requestAnimationFrame(() => {
      const hash = new URL(route.href, window.location.origin).hash;
      let fragment = hash.slice(1);
      try { fragment = decodeURIComponent(fragment); } catch { /* Preserve malformed fragments as literal IDs. */ }
      const target = hash ? document.getElementById(fragment) : null;
      if (target) target.scrollIntoView();
      else {
        window.scrollTo(0, 0);
        const heading = document.querySelector<HTMLElement>('#main-content h1');
        if (heading) {
          heading.tabIndex = -1;
          heading.focus({ preventScroll: true });
        }
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [route.href, pageComponent, loadError]);

  const Page = pageComponent;
  return (
    <AdPrivacyProvider>
      <div className="flex flex-col min-h-screen">
        <RouteMetadata path={route.href} />
        <a href="#main-content" className="skip-link">Skip to main content</a>
        <Header onNavigate={navigate} />
        <main id="main-content" className="flex-1">
          <PageTransition key={routeKey}>
            {loadError ? (
              <section role="alert" className="max-w-3xl mx-auto px-4 py-16">
                <h1 className="text-3xl mb-4">This page could not load</h1>
                <p className="mb-4">Check your connection and try again.</p>
                <button onClick={() => { needsFocus.current = true; setRetry(value => value + 1); }} className="px-4 py-2 bg-green-700 text-white rounded mr-4">Try again</button>
                <a href={route.href} className="text-green-700 underline">Reload page</a>
              </section>
            ) : Page ? (
              <Page onNavigate={navigate} airportId={route.id} searchQuery={route.search} slug={route.slug} />
            ) : (
              <div className="page-loading" role="status" aria-busy="true" aria-label="Loading page">Loading page…</div>
            )}
          </PageTransition>
        </main>
        <Footer onNavigate={navigate} />
      </div>
    </AdPrivacyProvider>
  );
}
